import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/Base/Select";

interface BulkEditActionBarProps {
  selectedCount: number;
  labels: string[];
  /** Resolves true when the label was applied; false keeps the picker as is. */
  onApplyLabel: (label: string) => Promise<boolean>;
  /**
   * When set, the bar offers a single "Edit links…" button (opens the shared
   * BulkLinkDialog, WEBV3-342) instead of the label-only picker + Apply.
   * Omit it to keep the legacy label-only bar.
   */
  onEditLinks?: () => void;
  onClearSelection: () => void;
  onExit: () => void;
  applying?: boolean;
}

export const BulkEditActionBar = ({
  selectedCount,
  labels,
  onApplyLabel,
  onEditLinks,
  onClearSelection,
  onExit,
  applying = false,
}: BulkEditActionBarProps) => {
  const [labelToApply, setLabelToApply] = useState("");

  if (selectedCount === 0) return null;

  const handleApply = async () => {
    if (!labelToApply) return;
    const applied = await onApplyLabel(labelToApply);
    if (applied) setLabelToApply("");
  };

  return (
    <div className="flex items-center gap-2 py-2">
      <span className="text-sm font-medium">{selectedCount} selected</span>
      {onEditLinks ? (
        <Button variant="accent" onClick={onEditLinks}>
          Edit links…
        </Button>
      ) : (
        <>
          <Select
            value={labelToApply || "placeholder"}
            onValueChange={setLabelToApply}
          >
            <SelectTrigger className="h-9 w-48">
              <SelectValue placeholder="Apply a label" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value="placeholder"
                disabled
                className="text-gray-400"
              >
                Apply a label
              </SelectItem>
              {labels.map((label) => (
                <SelectItem key={label} value={label}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="accent"
            disabled={!labelToApply || applying}
            onClick={handleApply}
          >
            Apply
          </Button>
        </>
      )}
      <Button variant="outline" onClick={onClearSelection}>
        Clear Selection
      </Button>
      <Button variant="outline" onClick={onExit}>
        Exit Bulk Edit
      </Button>
    </div>
  );
};
